import { Logger } from "@cede/utils";
import { Address } from "@ton/core";

/**
 * Converts a TON address to user-friendly format
 * @param address - TON address in any format (hex, raw, user-friendly, etc.)
 * @returns User-friendly address
 */
export const toFriendlyAddress = (address: string): string => {
  try {
    const parsedAddress = Address.parse(address);
    return parsedAddress.toString({ bounceable: false, urlSafe: true, testOnly: false });
  } catch (error) {
    Logger.error("Failed to parse TON address", error);
    return address; // Return original address if parsing fails
  }
};

/**
 * Formats address for display based on the identity provider
 * For TON addresses, converts to bounceable format
 * @param address - Address to format (can be hex format like "0:abc123..." or any other format)
 * @param identityProvider - The authentication provider ('ton' or 'privy')
 * @returns Formatted address
 */
export const formatAddressForDisplay = (address: string, identityProvider: string): string => {
  if (identityProvider === "ton") {
    return toFriendlyAddress(address);
  }
  return address; // For other providers, return as-is
};

export const formatAddressForTableDisplay = (address: string, identityProvider: string): string => {
  if (identityProvider === "ton") {
    return isEVMAddress(address) ? "Browser User" : toFriendlyAddress(address);
  }

  return isEVMAddress(address) ? address : "Telegram User"; // For other providers, return as-is
};

export const isEVMAddress = (address: string): boolean => {
  return address.startsWith("0x");
};

export const truncateAddress = (address: string): string =>
  address.length > 12 ? `${address.slice(0, 6)}...${address.slice(-4)}` : address;
